// Message protection utilities for filtering and masking sensitive information
import { sanitizeText } from './inputSanitization';

// Patterns for detecting sensitive information
const SENSITIVE_PATTERNS = {
  // Phone number patterns (various formats)
  phone: [
    /(\+?\d{1,4}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g, // US format
    /(\+?\d{1,3}[-.\s]?)?\d{10,}/g, // International format
    /(\+?2\d{1,2}[-.\s]?)?\d{8,11}/g, // Egyptian/Middle East format
    /\b\d{4}[-.\s]?\d{3}[-.\s]?\d{4}\b/g, // Generic format
  ],
  
  // Email patterns
  email: [
    /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
    /\b[A-Za-z0-9._%+-]+\s*@\s*[A-Za-z0-9.-]+\s*\.\s*[A-Z|a-z]{2,}\b/g, // With spaces
  ],
  
  // Social media usernames/handles
  social: [
    /@[A-Za-z0-9_]+/g, // Twitter/X handles
    /instagram\.com\/[A-Za-z0-9_.]+/gi,
    /facebook\.com\/[A-Za-z0-9_.]+/gi,
    /telegram\.me\/[A-Za-z0-9_]+/gi,
    /wa\.me\/[+\d]+/gi, // WhatsApp links
  ],
  
  // URLs that might contain personal info
  personalUrls: [
    /https?:\/\/[^\s]+/g,
  ],
  
  // National ID patterns (Egypt example)
  nationalId: [
    /\b[23]\d{13}\b/g, // Egyptian national ID format
  ],
  
  // Credit card patterns
  creditCard: [
    /\b\d{4}[-.\s]?\d{4}[-.\s]?\d{4}[-.\s]?\d{4}\b/g,
  ],
  
  // Address patterns (basic)
  address: [
    /\b\d+\s+[A-Za-z\s]+(?:street|st|avenue|ave|road|rd|drive|dr|lane|ln|blvd|boulevard)\b/gi,
  ]
};

// Replacement patterns for masking
const REPLACEMENT_MASKS = {
  phone: '[PHONE_NUMBER]',
  email: '[EMAIL_ADDRESS]',
  social: '[SOCIAL_HANDLE]',
  personalUrls: '[LINK]',
  nationalId: '[ID_NUMBER]',
  creditCard: '[CARD_NUMBER]',
  address: '[ADDRESS]'
};

// Warning messages for different types of sensitive content
const WARNING_MESSAGES = {
  phone: 'Phone number detected and masked for privacy',
  email: 'Email address detected and masked for privacy',
  social: 'Social media handle detected and masked for privacy',
  personalUrls: 'URL detected and masked for privacy',
  nationalId: 'ID number detected and masked for privacy',
  creditCard: 'Card number detected and masked for privacy',
  address: 'Address detected and masked for privacy'
};

interface ProtectionResult {
  maskedContent: string;
  originalContent: string;
  detectedSensitiveInfo: string[];
  isClean: boolean;
}

export const protectMessageContent = (content: string): ProtectionResult => {
  // First sanitize the input
  const sanitizedContent = sanitizeText(content);
  let maskedContent = sanitizedContent;
  const detectedSensitiveInfo: string[] = [];
  
  // Check for each type of sensitive information
  Object.entries(SENSITIVE_PATTERNS).forEach(([type, patterns]) => {
    patterns.forEach(pattern => {
      if (pattern.test(maskedContent)) {
        maskedContent = maskedContent.replace(pattern, REPLACEMENT_MASKS[type as keyof typeof REPLACEMENT_MASKS]);
        if (!detectedSensitiveInfo.includes(type)) {
          detectedSensitiveInfo.push(type);
        }
      }
    });
  });
  
  const isClean = detectedSensitiveInfo.length === 0;
  
  return {
    maskedContent,
    originalContent: sanitizedContent,
    detectedSensitiveInfo,
    isClean
  };
};

// Function to check if message contains sensitive info without processing
export const containsSensitiveInfo = (content: string): boolean => {
  return Object.values(SENSITIVE_PATTERNS).some(patterns =>
    patterns.some(pattern => pattern.test(content))
  );
};

// Get warning message for detected sensitive content types
export const getSensitivityWarnings = (detectedTypes: string[]): string[] => {
  return detectedTypes.map(type => WARNING_MESSAGES[type as keyof typeof WARNING_MESSAGES]);
};

// Enhanced validation for message content
export const validateMessageContent = (content: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  // Check length
  if (content.length > 1000) {
    errors.push('Message is too long (max 1000 characters)');
  }
  
  if (content.trim().length === 0) {
    errors.push('Message cannot be empty');
  }
  
  // Check for suspicious patterns
  const suspiciousPatterns = [
    { pattern: /<script/i, message: 'Script tags are not allowed' },
    { pattern: /javascript:/i, message: 'JavaScript URLs are not allowed' },
    { pattern: /on\w+=/i, message: 'Event handlers are not allowed' },
    { pattern: /data:text\/html/i, message: 'HTML data URLs are not allowed' },
    { pattern: /vbscript:/i, message: 'VBScript is not allowed' },
    { pattern: /<iframe/i, message: 'Iframes are not allowed' },
    { pattern: /<object/i, message: 'Object tags are not allowed' },
    { pattern: /<embed/i, message: 'Embed tags are not allowed' }
  ];
  
  suspiciousPatterns.forEach(({ pattern, message }) => {
    if (pattern.test(content)) {
      errors.push(message);
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Function to safely display message content (additional protection layer)
export const safeDisplayContent = (maskedContent: string): string => {
  // Additional sanitization for display
  return maskedContent
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};